import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './Offer.css';
import SessionStorage from '../../utils/SessionStorage';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { IProgramRates } from '../../types/responses/PrequalResponse';
import { sanitizeHtml } from '../../utils/cob-utils';
import { useWidthContext } from '../../providers/Width';
import { Icon } from '../../types/shared/Icon';
import { IOfferModuleCF, TOfferModuleBenefit } from '../../types/shared/OfferModule';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { getProgramData } from '../../utils/acquisition-utils';
import axios, { AxiosResponse } from 'axios';
import { flowDataModule } from '../../utils/flowDataModule';
import { usePersonalizationCustomizationContext } from '../../providers/PersonalizationCustomizationProvider/PersonalizationCustomizationProvider';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import { CardContainer, Section } from '../../importCL';

interface IOfferProps extends MappedComponentProperties {
    benefitIcon?: Icon;
    topBorderAdded?: boolean;
    bottomBorderAdded?: boolean;
    cardNameToggle?: boolean;
    headline?: string;
    headerStatement?: string;

    useAuthoredOfferText?: boolean;
    offerText?: string;

    disclaimerText?: string;

    // appOfferModule CF
    defaultOffer?: IOfferModuleCF;

    // annualFeeMessage CF
    scenarioOneMessage?: string;
    scenarioTwoMessage?: string;
    scenarioThreeMessage?: string;

    complexAnnualFeeContentFragmentPath?: string;
    version?: string;
    usingNewStyle?: boolean;
    counterHeadline?: string;
}

type TRateData = {
    aprText: string;
    annualFeeText: string;
    isComplexAnnualfee?: boolean;
}

export type TComplexAnnualFeeServletResponse = {
    Error: unknown,
    complexAnnualFeeText: string,
};

export const OfferEditConfig = {
    emptyLabel: 'Offer',
    isEmpty: (props: PropsWithChildren<IOfferProps>) => {
        return !props || !props.headline;
    },
};

export const createAnnualFeeMessage = (
    programRates: IProgramRates,
    scenarios: {
        scenarioOneMessage?: string,
        scenarioTwoMessage?: string,
        scenarioThreeMessage?: string
    }
): string => {
    let annualFeeMessage: string = '';
    const { IntroAnnualMembership, AnnualMembership, BilledMonthlyMembership } = programRates;

    if (IntroAnnualMembership === AnnualMembership && BilledMonthlyMembership === 0) {
        annualFeeMessage = scenarios.scenarioOneMessage;
    } else if (IntroAnnualMembership !== AnnualMembership && BilledMonthlyMembership === 0) {
        annualFeeMessage = scenarios.scenarioTwoMessage;
    } else if (IntroAnnualMembership > 0 && AnnualMembership > 0 && BilledMonthlyMembership > 0) {
        annualFeeMessage = scenarios.scenarioThreeMessage;
    }

    if (!annualFeeMessage) {
        return '';
    }

    return annualFeeMessage
        .replace(/{{IntroAnnualMembership}}/g, IntroAnnualMembership.toString())
        .replace(/{{AnnualMembership}}/g, AnnualMembership.toString())
        .replace(/{{BilledMonthlyMembership}}/g, BilledMonthlyMembership.toString());
};

const Offer = (props: PropsWithChildren<IOfferProps>): JSX.Element => {
    const { width } = useWidthContext();
    const { isApplicationInfoRecieved } = useAcquisitionContext();
    const [offerInfo, setOfferInfo] = useState<IOfferModuleCF & TRateData>({
        annualFeeText: '',
        aprText: '',
        bazaarvoiceReviewsProductId: '',
        benefits: [],
        cardImageAltText: '',
        cardImagePath: '',
        counterCardImagePath: '',
        counterCardImageAltText: '',
        premiumCardDisclosure: '',
        cardname: '',
        headline: '',
        overwriteHeadline: false,
        overwriteRibbon: false,
        partnerId: '',
        ribbonText: '',
        tags: [],
        disclaimer: '',
    });
    const programCode: string = SessionStorage.tryGet('selectedProgramCode', '');
    const personalizationData = JSON.parse(PathUtils.getMetaPropertyValue('AcquisitionPersonalization') || '{}') as { usePersonalization?: boolean, ignoreSourceIds?: string[] };

    const isAuthor = (window.location.host.indexOf('4502') > -1) || (window.location.host.includes('localhost'));

    const { setActivePersonalizations } = usePersonalizationCustomizationContext();

    const handlePersonalization = async (): Promise<void> => {
        const params = new URLSearchParams(window.location.search);
        if (
            personalizationData?.usePersonalization &&
            !personalizationData?.ignoreSourceIds.includes(params.get('C1BSourceID')) &&
            offerInfo?.headline?.includes('{CustomPersonalization}')
        ) {
            const firstName = (await flowDataModule.getFromSession())?.FirstName || await SessionStorage.getCipher('personalInfo', true).then(personalInfo => personalInfo.FirstName).catch(() => null) || '';
            setOfferInfo((prev) => ({ ...prev, headline: prev.headline.replace(/{CustomPersonalization}/g, firstName) }));
            setActivePersonalizations((prev) => ({ ...prev, OfferHeadline: true }));

        } else if (offerInfo?.headline?.includes('{CustomPersonalization}')) {
            setOfferInfo((prev) => ({ ...prev, headline: prev.headline?.replace(/{CustomPersonalization}/g, '') }));
        }
    };

    const fetchComplexAnnualFee = (): void => {
        const path = props.complexAnnualFeeContentFragmentPath;
        if (!props.complexAnnualFeeContentFragmentPath || !programCode || isAuthor) {
            return;
        }

        axios.get(`/services/ComplexAnnualFeeServlet.path=${path}.programCode=${programCode}`)
            .then((res: AxiosResponse<TComplexAnnualFeeServletResponse, unknown>) => res.data)
            .then((data: TComplexAnnualFeeServletResponse) => {
                if (data?.Error) {
                    // eslint-disable-next-line no-console
                    console.log(data.Error);
                }

                if (data?.complexAnnualFeeText) {
                    setOfferInfo(prev => ({
                        ...prev,
                        isComplexAnnualfee: true,
                        annualFeeText: data.complexAnnualFeeText,
                    }));
                }
            })
            .catch((err: unknown) => {
                // eslint-disable-next-line no-console
                console.log(err);
            });
    };

    const createAprMessage = useCallback((primeRate: Object | null, programRates: IProgramRates): string => {
        if (!programRates || isAuthor) {
            return '';
        }

        const calculatedRate = programRates.CalculatedPurchaseRate;
        const baseRate = programRates.BaseRate;
        const isVariableRate = primeRate && (baseRate !== calculatedRate);

        return `Purchase APR is ${calculatedRate}%${isVariableRate ? ' variable' : ''}.<sup>†</sup>`;
    }, [isAuthor]);

    useEffect((): void => {
        getProgramData()
            .then(async (rateData: Record<string, IProgramRates>): Promise<void> => {
            const hasScenarioMessages = props.scenarioOneMessage || props.scenarioTwoMessage || props.scenarioThreeMessage;
            const hasComplexAnnualFee = props.complexAnnualFeeContentFragmentPath;
            const shouldShowRateInfo = hasScenarioMessages || hasComplexAnnualFee;
            const annualFeeText = Object.keys(rateData).length && hasScenarioMessages ? createAnnualFeeMessage(rateData.ProgramRates, { ...props }) : '';
            const rateDataObject: TRateData = {
                aprText: shouldShowRateInfo && Object.keys(rateData).length ? createAprMessage(rateData.PrimeRate, rateData.ProgramRates) : '',
                annualFeeText: annualFeeText,
            };

            const dynamicContent = SessionStorage.getObject('offerContent');

            if (!dynamicContent || Object.keys(dynamicContent).length < 1) {
                setOfferInfo({
                    ...props.defaultOffer,
                    ...rateDataObject,
                    headline: props.headline,
                    cardname: `${props.defaultOffer?.cardname ? sanitizeHtml(props.defaultOffer?.cardname) : ''} ${props.useAuthoredOfferText ? transformHTMLParserLinks(props.offerText ?? '') : 'features and offer:*'}`,
                    premiumCardDisclosure: props.defaultOffer?.premiumCardDisclosure,
                });
            } else {
                const isPQCounterOffer = window.location.href.includes('/pre-qualification/pre-qualification-result/counter-single-offer');
                const cardPath = isPQCounterOffer && dynamicContent.counterCardImagePath ? dynamicContent.counterCardImagePath : dynamicContent.cardImagePath;
                const cardAlt = isPQCounterOffer && dynamicContent.counterCardImageAltText ? dynamicContent.counterCardImageAltText : dynamicContent.cardImageAltText;
                setOfferInfo({
                    ...props.defaultOffer!,
                    ...rateDataObject,
                    cardname: `${dynamicContent.cardname ? sanitizeHtml(dynamicContent.cardname) : ''} ${props.useAuthoredOfferText ? transformHTMLParserLinks(props.offerText ?? '') : 'features and offer:*'}`,
                    cardImagePath: cardPath,
                    cardImageAltText: cardAlt,
                    premiumCardDisclosure: dynamicContent.premiumCardDisclosure as string,
                    headline: dynamicContent.headline || props.headline,
                    benefits: dynamicContent.benefits as TOfferModuleBenefit[],
                    disclaimer: dynamicContent.disclaimer,
                });
            }
            await handlePersonalization();

            // will override annualFeeText in OfferInfo if exists
            fetchComplexAnnualFee();
        });
    }, [isApplicationInfoRecieved]);

    useEffect((): void => {
        handlePersonalization();
    }, [props, offerInfo]);

    return (
            (props.usingNewStyle && window.location.href.includes('/counter')) 
        ? (
            <Section id='offer' className='Offer__counter'>
                <CardContainer className='Offer__container' borderSettings={{
                    borderRadiusMedium: true,
                }}>
                    <div className='Offer__header'>
                        {offerInfo?.cardImagePath &&
                            <div className='Offer__imageDisclosure'>
                                <img className='Offer__counterCardImage' src={offerInfo?.cardImagePath} alt={offerInfo?.cardImageAltText} />
                                {offerInfo.premiumCardDisclosure &&
                                    <div className='Offer__disclosure'>
                                        {transformHTMLParserLinks(offerInfo.premiumCardDisclosure)}
                                    </div>
                                }
                            </div>
                        }
                        <h2 className='Offer__counterHeadline'>
                            {transformHTMLParserLinks(props.counterHeadline)}
                        </h2>
                    </div>
                    <h2 className='Offer__counterSubHeadline'>
                        {transformHTMLParserLinks(offerInfo.headline)}
                    </h2>
                    {props.headerStatement &&
                        <div className='Offer__counterHeaderStatement'>
                            {transformHTMLParserLinks(props.headerStatement)}
                        </div>
                    }
                    {(!props.cardNameToggle || width > 600) && props.version !== 'skip-pq' &&
                        <h3 className='Offer__counterBenefitsTitle'>
                            {transformHTMLParserLinks(offerInfo?.cardname)}
                        </h3>
                    }
                    <div className='Offer__counterBenefits'>
                        {offerInfo?.benefits && offerInfo.benefits.map((benefit, i) => {
                            return (
                                <div className='Offer__benefit' key={`Offer__benefit${i}`}>
                                    <img className='Offer__checkIcon'
                                        src={props.benefitIcon?.iconPath}
                                        alt={props.benefitIcon?.iconAltText} />
                                    <div>
                                        <h4 className='Offer__benefitTitle'>
                                            {transformHTMLParserLinks(benefit.title)}
                                        </h4>
                                        <p>{transformHTMLParserLinks(benefit.subTitle)}</p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {(offerInfo?.aprText || offerInfo?.annualFeeText) && (
                        <div className='Offer__subheadline'>
                            {offerInfo?.aprText && (
                                <span className='Offer__apr'>
                                    {transformHTMLParserLinks(offerInfo?.aprText)}
                                </span>
                            )}
                            {offerInfo?.annualFeeText && (
                                <span className={`Offer__annualFee${offerInfo.isComplexAnnualfee ? '--complexFee' : ''}`}>
                                    {transformHTMLParserLinks(offerInfo?.annualFeeText)}
                                </span>
                            )}
                        </div>
                    )}
                    {(offerInfo?.disclaimer || props.disclaimerText) && (
                        <div className='Offer__counterDisclaimer'>
                            {transformHTMLParserLinks(offerInfo?.disclaimer || props.disclaimerText)}
                        </div>
                    )}
                </CardContainer>
            </Section>
        ) : (
            <Section id='offer' className={`Offer ${props.version === 'skip-pq' ? 'Offer--skipPq' : ''}`}>
                {props.topBorderAdded && <hr />}
                {width <= 600 &&
                    <>
                        <h2 className={`Offer__headline ${props.version === 'skip-pq' ? 'Offer__headline--skipPq' : ''}`}>
                            {transformHTMLParserLinks(offerInfo.headline)}
                        </h2>

                        {props.headerStatement &&
                            <div className={'Offer__headerStatement'}>
                                {transformHTMLParserLinks(props.headerStatement)}
                            </div>
                        }

                        {offerInfo?.cardImagePath &&
                            <div className={`Offer__imageDisclosure${props.version === 'skip-pq' ? '--skipPq' : ''}`}>
                                <img className='Offer__cardImage' src={offerInfo?.cardImagePath} alt={offerInfo?.cardImageAltText} />
                                {offerInfo.premiumCardDisclosure &&
                                    <div className='Offer__disclosure'>
                                        {transformHTMLParserLinks(offerInfo.premiumCardDisclosure)}
                                    </div>
                                }
                            </div>
                        }
                    </>
                }
                {width > 600 && (
                    <>
                        <h2 className={`Offer__headline ${props.version === 'skip-pq' ? 'Offer__headline--skipPq' : ''}`}>
                            {transformHTMLParserLinks(offerInfo.headline)}
                        </h2>

                        {props.headerStatement &&
                            <div className={'Offer__headerStatement'}>
                                {transformHTMLParserLinks(props.headerStatement)}
                            </div>
                        }
                    </>
                )}
                {(!props.cardNameToggle || width > 600) && props.version !== 'skip-pq' &&
                    <h3 className='Offer__benefitsTitle'>
                        {transformHTMLParserLinks(offerInfo?.cardname)}
                    </h3>
                }
                <div className={`Offer__benefits ${props.version !== 'skip-pq' ? 'pq' : ''}`}>
                    {offerInfo?.benefits && offerInfo.benefits.map((benefit, i) => {
                        return (
                            <div className='Offer__benefit' key={`Offer__benefit${i}`}>
                                <img className='Offer__checkIcon'
                                    src={props.benefitIcon?.iconPath}
                                    alt={props.benefitIcon?.iconAltText} />
                                <div>
                                    <h4 className='Offer__benefitTitle'>
                                        {transformHTMLParserLinks(benefit.title)}
                                    </h4>
                                    <p>{transformHTMLParserLinks(benefit.subTitle)}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {width > 600 && offerInfo?.cardImagePath && (
                    <div className='Offer__imageDisclosure'>
                        <img className={`Offer__cardImage ${offerInfo.benefits && offerInfo.benefits.length < 3 ? 'Offer__cardImage--extraMargin' : ''}`} src={offerInfo?.cardImagePath} alt={offerInfo?.cardImageAltText} />
                        {offerInfo.premiumCardDisclosure &&
                            <div className='Offer__disclosure'>
                                {transformHTMLParserLinks(offerInfo.premiumCardDisclosure)}
                            </div>
                        }
                    </div>
                )}
                {(offerInfo?.aprText || offerInfo?.annualFeeText) && (
                    <div className='Offer__subheadline'>
                        {offerInfo?.aprText && (
                            <span className='Offer__apr'>
                                {transformHTMLParserLinks(offerInfo?.aprText)}
                            </span>
                        )}
                        {offerInfo?.annualFeeText && (
                            <span className={`Offer__annualFee${offerInfo.isComplexAnnualfee ? '--complexFee' : ''}`}>
                                {transformHTMLParserLinks(offerInfo?.annualFeeText)}
                            </span>
                        )}
                    </div>
                )}
                {(offerInfo?.disclaimer || props.disclaimerText) && (
                    <div className='Offer__disclaimer'>
                        {transformHTMLParserLinks(offerInfo?.disclaimer || props.disclaimerText)}
                    </div>
                )}
                {props.bottomBorderAdded && <hr />}
            </Section>
        ));
};

export default Offer;
